import { useState, useEffect } from 'preact/hooks';
import { MainLayout } from '../../components/MainLayout';
import { getAuthHeaders } from '../../utils/auth';

interface Team {
  teamId: string;
  name: string;
  description?: string;
  memberCount: number;
  status: 'active' | 'deactivated';
}

interface TeamData {
  team: Team;
  userRole: 'admin' | 'member';
  permissions: any;
}

interface PendingInvitation {
  invitationId: string;
  email: string;
  role: string;
  createdAt: string;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'declined' | 'canceled' | 'expired';
}

interface TeamInvitePageProps {
  teamId: string;
}

export function TeamInvitePage({ teamId }: TeamInvitePageProps) {
  const [teamData, setTeamData] = useState<TeamData | null>(null);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadTeamDetails();
    loadPendingInvitations();
  }, [teamId]);

  const loadTeamDetails = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load team details');
      }

      const data = await response.json();
      setTeamData(data);
    } catch (err) {
      console.error('Error loading team details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team');
    }
  };

  const loadPendingInvitations = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/invitations`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      }
    } catch (err) {
      console.error('Error loading pending invitations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: Event) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setInviting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          email: email.trim(),
          role
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send invitation');
      }

      setSuccess(`Invitation sent to ${email}`);
      setEmail('');
      setRole('member');

      // Reload pending invitations
      await loadPendingInvitations();
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string, email: string) => {
    if (!confirm(`Cancel invitation for ${email}?`)) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel invitation');
      }

      setSuccess(`Invitation for ${email} has been cancelled`);
      await loadPendingInvitations();
    } catch (err) {
      console.error('Error cancelling invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
    }
  };

  const handleReinvite = async (email: string, role: string) => {
    if (!confirm(`Send a new invitation to ${email}?`)) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          email,
          role
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send invitation');
      }

      setSuccess(`New invitation sent to ${email}`);
      await loadPendingInvitations();
    } catch (err) {
      console.error('Error sending reinvitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    }
  };

  const getInvitationStatus = (invitation: PendingInvitation) => {
    if (invitation.status === 'accepted') {
      return <span class="badge badge-success badge-sm">Accepted</span>;
    }
    if (invitation.status === 'declined') {
      return <span class="badge badge-error badge-sm">Declined</span>;
    }
    if (invitation.status === 'canceled') {
      return <span class="badge badge-ghost badge-sm">Canceled</span>;
    }
    if (new Date(invitation.expiresAt) < new Date()) {
      return <span class="badge badge-error badge-sm">Expired</span>;
    }
    return <span class="badge badge-warning badge-sm">Pending</span>;
  };

  if (loading) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
        <div class="container mx-auto p-8 max-w-4xl">
          <div class="flex items-center justify-center h-64">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
          </div>
      </div>
    </MainLayout>
  );
}

  if (error && !teamData) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div class="container mx-auto p-8 max-w-4xl">
            <div class="alert alert-error mb-4">
              <span>{error}</span>
            </div>
            <a href="/teams" class="btn btn-ghost">← Back to Teams</a>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!teamData || teamData.userRole !== 'admin') {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div class="container mx-auto p-8 max-w-4xl">
            <div class="alert alert-error mb-4">
              <span>You don't have permission to invite members to this team</span>
            </div>
            <a href={`/teams/${teamId}`} class="btn btn-ghost">← Back to Team</a>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">

      <div class="container mx-auto p-8 max-w-4xl">
        {/* Header */}
        <div class="flex items-center gap-4 mb-8">
          <a href={`/teams/${teamId}`} class="btn btn-ghost btn-sm">← Back to Team</a>
          <div>
            <h1 class="text-3xl font-bold">Invite Members</h1>
            <p class="text-base-content/70 mt-2">
              Invite new members to join {teamData.team.name}
            </p>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div class="alert alert-error mb-6">
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div class="alert alert-success mb-6">
            <span>{success}</span>
          </div>
        )}

        {/* Invite Form */}
        <div class="card bg-base-100 shadow-md mb-6">
          <div class="card-body">
            <h2 class="card-title mb-4">Send Invitation</h2>

            <form onSubmit={handleInvite}>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text">Email Address *</span>
                  </label>
                  <input
                    type="email"
                    placeholder="colleague@example.com"
                    class="input input-bordered"
                    value={email}
                    onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
                    required
                  />
                </div>

                <div class="form-control">
                  <label class="label">
                    <span class="label-text">Role</span>
                  </label>
                  <select
                    class="select select-bordered"
                    value={role}
                    onChange={(e) => setRole((e.target as HTMLSelectElement).value as 'admin' | 'member')}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div class="bg-base-200 p-4 rounded-lg mb-6">
                <div class="text-sm text-base-content/70">
                  <p class="mb-2"><strong>Member:</strong> Can participate in team activities and view team resources</p>
                  <p><strong>Admin:</strong> Can manage team settings, invite/remove members, and has all member permissions</p>
                </div>
              </div>

              <div class="flex justify-end">
                <button
                  type="submit"
                  class="btn btn-primary"
                  disabled={inviting}
                >
                  {inviting ? (
                    <>
                      <span class="loading loading-spinner loading-sm"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Invitation'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Invitations */}
        {invitations.length > 0 && (
          <div class="card bg-base-100 shadow-md">
            <div class="card-body">
              <h2 class="card-title mb-4">Invitations</h2>

              <div class="overflow-x-auto">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Sent</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((invitation) => (
                      <tr key={invitation.invitationId}>
                        <td>{invitation.email}</td>
                        <td>
                          <span class="capitalize">{invitation.role}</span>
                        </td>
                        <td>{getInvitationStatus(invitation)}</td>
                        <td>{new Date(invitation.createdAt).toLocaleDateString()}</td>
                        <td>{new Date(invitation.expiresAt).toLocaleDateString()}</td>
                        <td>
                          {invitation.status === 'pending' && (
                            <button
                              class="btn btn-ghost btn-xs text-error"
                              onClick={() => handleCancelInvitation(invitation.invitationId, invitation.email)}
                            >
                              Cancel
                            </button>
                          )}
                          {(invitation.status === 'declined' || invitation.status === 'canceled') && (
                            <button
                              class="btn btn-ghost btn-xs text-primary"
                              onClick={() => handleReinvite(invitation.email, invitation.role)}
                            >
                              Reinvite
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </MainLayout>
  );
}
